import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "@/ui-components/button-with-loading";

type PropTypes = {
  isOpen: boolean;
  onClose: () => void;
  onConfirmDelete?: () => Promise<void>;
};

function DeleteConfirmationDialog(props: PropTypes) {
  const { isOpen, onClose } = props;
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!props.onConfirmDelete) return;

    setLoading(true);

    try {
      await new Promise((res) => setTimeout(res, 2000));

      await props.onConfirmDelete();

      toast.success("Resource deleted successfully.");
      onClose();
    } catch (error) {
      toast.error(
        "There was an error deleting the resource, please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10 no-drag">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/50 dark:bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center  sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                <ExclamationTriangleIcon
                  aria-hidden="true"
                  className="size-6 text-red-600"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900 dark:text-gray-100"
                >
                  Delete Headline
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Are you sure you want to delete this headline? Once removed,
                    this headline will be permanently deleted from the system
                    and cannot be recovered. Please confirm if you wish to
                    proceed with the deletion.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
              <button
                onClick={handleDelete}
                disabled={loading}
                className="text-sm px-3 py-2 rounded-md border border-red-600 bg-red-900/20 text-red-400 hover:border-red-500 hover:text-red-300 hover:bg-red-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Deleting..." : "Yes, delete"}
              </button>

              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default DeleteConfirmationDialog;
